import {
  DAP_INTERVENTIONS_SECTIONS,
  DAP_PATIENT_RESPONSE_SECTIONS,
  OTHER_INTERVENTIONS_NOTE_KEY,
} from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/dap-note/DapNoteConstant";
import { ExamCheckboxSection } from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";
import AssessmentBlock from "../_shared/AssessmentBlock";
import CptCodesBlock from "../_shared/CptCodesBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import {
  CarePlanTemplateList,
  OrderList,
  RenderTextWithContainerPattern,
  ReportContainer,
  ReportSection,
  TherapyNotesList,
} from "../constant";
// Shared mapper for the MSE and Risk Assessment modals — the DAP note reuses
// both of them (and their keys) from the Psychotherapy Note.
import InitialPsychModalsMapper from "../initialPsychEvolutionTemplate/components/InitialPsychModalsMapper";

interface DapNoteTemplateProps {
  data: any;
}

// The checkbox modals persist each section as a comma-separated string of the
// selected option names, e.g. { interventionsProvided: "dbtSkills,psychoeducation" }.
const parseSelected = (value: unknown): string[] =>
  typeof value === "string"
    ? value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
    : [];

interface CheckboxSectionBlockProps {
  data: any;
  label: string;
  sections: ExamCheckboxSection[];
  // Trailing free text not tied to an option, e.g. "Other Therapeutic
  // Interventions".
  extraNoteKey?: string;
  extraNoteLabel?: string;
}

const CheckboxSectionBlock = ({
  data,
  label,
  sections,
  extraNoteKey,
  extraNoteLabel,
}: CheckboxSectionBlockProps) => {
  const rendered = sections
    .map((section) => {
      const selected = parseSelected(data?.[section.key]);
      const labels = section.items
        .filter((it) => selected.includes(it.name))
        .map((it) => it.label);
      const note = (data?.[section.noteKey ?? `${section.key}Note`] ??
        "") as string;
      return { key: section.key, labels, note };
    })
    .filter((s) => s.labels.length > 0 || s.note.trim());

  const extraNote = extraNoteKey
    ? ((data?.[extraNoteKey] ?? "") as string)
    : "";

  if (!rendered.length && !extraNote.trim()) return null;

  return (
    <ReportContainer label={label}>
      {rendered.map((s) => (
        <div key={s.key} style={{ marginBottom: 8, paddingLeft: 5 }}>
          {s.labels.length > 0 && (
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              {s.labels.join(", ")}
            </p>
          )}
          {s.note.trim() && (
            <p style={{ margin: "2px 0", fontSize: "14px" }}>
              <strong>Note : </strong>
              {s.note}
            </p>
          )}
        </div>
      ))}
      {extraNote.trim() && (
        <p style={{ margin: "6px 0", paddingLeft: 5, fontSize: "14px" }}>
          <strong>{`${extraNoteLabel} : `}</strong>
          {extraNote}
        </p>
      )}
    </ReportContainer>
  );
};

/**
 * DAP (Data, Assessment, Plan) Progress Note report.
 *
 * The Chief Complaint is printed by <EncounterSummeryTemplate/> for every note
 * type, so it is not repeated here. Screening assessments are printed under
 * Assessment, where the DAP form groups them, rather than under the Objective
 * section the other note templates use.
 */
const DapNoteTemplate = ({ data }: DapNoteTemplateProps) => {
  return (
    <>
      {/* ═══════════════ DATA ═══════════════ */}
      <ReportSection label="Data">
        <RenderTextWithContainerPattern label="Data" val={data?.dataFreeNote} />
        {/* MSE: checked options + per-section note + overall note. */}
        <InitialPsychModalsMapper data={data} type="mse" />
        <CheckboxSectionBlock
          data={data}
          label="Interventions Provided"
          sections={DAP_INTERVENTIONS_SECTIONS}
          extraNoteKey={OTHER_INTERVENTIONS_NOTE_KEY}
          extraNoteLabel="Other Therapeutic Interventions"
        />
        <CheckboxSectionBlock
          data={data}
          label="Patient Response"
          sections={DAP_PATIENT_RESPONSE_SECTIONS}
        />
      </ReportSection>

      {/* ═══════════════ ASSESSMENT ═══════════════ */}
      <ReportSection label="Assessment">
        <AssessmentBlock data={data} />
        {/* Risk Assessment: selected risk level + free-text note. */}
        <InitialPsychModalsMapper data={data} type="riskAssessment" />
        {/* Screening Assessments sit under Assessment on the DAP form, so they
            are printed here rather than under Objective the way the other
            note templates print them. */}
        <ScreeningAssessmentBlock data={data} />
      </ReportSection>

      {/* ═══════════════ PLAN ═══════════════ */}
      <ReportSection label="Plan">
        <RenderTextWithContainerPattern label="Plan" val={data?.planFreeNote} />
        <PrescriptionsBlock data={data} />
        <VaccinesBlock data={data} />
        <OrderList orders={data?.patientOrders} />
        <CptCodesBlock data={data} />
        <RenderTextWithContainerPattern
          label="Care Plan"
          val={data?.carePlan}
        />
        <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
        <TherapyNotesList therapyNotes={data?.therapyNotes} />
        <RenderTextWithContainerPattern
          label="Instructions"
          val={data?.instruction}
        />
      </ReportSection>

      {/* ═══════════════ FOLLOW-UP ═══════════════
          Fed by the DAP form's own Follow Up & Instructions field, and by the
          "Visit Note Template" button, which can push one into any note type.
          Printed the same way every other note template does. */}
      <ReportSection label="Follow-Up">
        <RenderTextWithContainerPattern
          label="Follow Up"
          val={data?.followUp}
        />
      </ReportSection>
    </>
  );
};

export default DapNoteTemplate;
